import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import { useNavigate } from 'react-router-dom';
import { useCommunity } from '../../contexts/CommunityContext';
import type { InvitePreview } from '../../services/communityService';
import { getCommunityErrorMessage } from '../../utils/communityApiErrors';

function statusMessage(preview: InvitePreview): string | null {
  if (preview.invite_status === 'valid') return null;
  if (preview.invite_status === 'already_member') {
    return "You're already a member of this community.";
  }
  if (preview.invite_status === 'expired') {
    return 'This invitation link has expired. Ask your admin for a new link.';
  }
  if (preview.invite_status === 'exhausted') {
    return 'This invitation link has already been used. Ask your admin for a new link.';
  }
  if (preview.invite_status === 'revoked') {
    return 'This invitation link is no longer valid.';
  }
  return 'This invitation link is not valid.';
}

interface Props {
  open: boolean;
  token: string;
  preview: InvitePreview | null;
  previewLoading?: boolean;
  onClose: () => void;
}

export const CommunityInviteJoinModal: React.FC<Props> = ({
  open,
  token,
  preview,
  previewLoading = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const { redeemInviteAndJoin } = useCommunity();
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const handleClose = () => {
    if (joining) return;
    setJoinError(null);
    onClose();
  };

  const handlePrimary = async () => {
    if (!token || !preview) return;

    if (preview.invite_status === 'already_member' && preview.community_id) {
      handleClose();
      navigate(`/community/${preview.community_id}`, { replace: true });
      return;
    }

    if (preview.invite_status !== 'valid') return;

    setJoining(true);
    setJoinError(null);
    try {
      const community = await redeemInviteAndJoin(token, { skipSelect: true });
      handleClose();
      navigate(`/community/${community.community_id}`, { replace: true });
    } catch (err: unknown) {
      setJoinError(getCommunityErrorMessage(err, 'Failed to join community.'));
    } finally {
      setJoining(false);
    }
  };

  const statusErr = preview ? statusMessage(preview) : null;
  const statusSeverity =
    preview?.invite_status === 'already_member' ? 'info' : 'warning';
  const canPrimary =
    preview?.invite_audience === 'app' &&
    (preview.invite_status === 'valid' || preview.invite_status === 'already_member');
  const primaryLabel =
    preview?.invite_status === 'already_member' ? 'Open community' : 'Join';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 600,
          pb: 1,
        }}
      >
        Join community
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={joining}
          size="small"
          sx={{ color: '#5f6368' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
        {previewLoading ? (
          <CircularProgress size={28} sx={{ my: 3, color: '#0f9d58' }} />
        ) : preview ? (
          <>
            <Avatar
              src={preview.community_avatar_url || undefined}
              sx={{ width: 64, height: 64, mx: 'auto', mb: 1.5, bgcolor: '#0f9d58' }}
            >
              <GroupsIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {preview.community_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {preview.invite_status === 'already_member'
                ? 'Open this community to continue chatting.'
                : 'You have been invited to join this community.'}
            </Typography>

            {statusErr && (
              <Alert severity={statusSeverity} sx={{ mb: 2, textAlign: 'left', borderRadius: 2 }}>
                {statusErr}
              </Alert>
            )}

            {preview.invite_audience === 'admin' && (
              <Alert severity="info" sx={{ mb: 2, textAlign: 'left', borderRadius: 2 }}>
                This invite is for CWY Admin Portal staff only.
              </Alert>
            )}

            {joinError && (
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left', borderRadius: 2 }}>
                {joinError}
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="error" sx={{ my: 1, textAlign: 'left', borderRadius: 2 }}>
            Could not load this invitation.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={joining} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handlePrimary()}
          disabled={!canPrimary || joining || previewLoading || !preview}
          sx={{
            borderRadius: 2,
            bgcolor: '#0f9d58',
            '&:hover': { bgcolor: '#0b8043' },
            minWidth: 120,
          }}
        >
          {joining ? <CircularProgress size={20} color="inherit" /> : primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
